'use client';

import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { PERMISSIONS } from '@repo/shared/domain';
import { EventEmitter, getVerifyPermission, guardedClient as request } from '@repo/shared/helpers';
import BadgesTab from 'src/parts/tabs/BadgesTab';
import { getUserBadges } from 'src/api/admin/badges';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { useConfig } from 'src/hooks/useConfig';
export const Route = createFileRoute('/_admin/users/$userId/badges')({
	validateSearch: (search: Record<string, unknown>) => {
		return {
			page: (search.page as number) || undefined,
			sort: (search.sort as string) || undefined,
		};
	},
	loaderDeps: ({ search }) => search,
	loader: async ({ params, deps }) => {
		const { userId } = params;
		const badges = await getUserBadges(userId, deps);
		return { badges };
	},
	component: BadgesTabPage,
});

function BadgesTabPage() {
	const { userId } = Route.useParams();
	const searchParams = Route.useSearch();
	const { config } = useConfig();
	const tenantSecret = config?.tenantSecret || '';
	const { badges: initialBadges } = Route.useLoaderData();
	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();

	const { data: badges, refetch: refetchBadges } = useQuery({
		queryKey: ['user-badges', userId, searchParams],
		queryFn: () => getUserBadges(userId, searchParams),
		initialData: initialBadges,
	});

	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const hasFullAccess = verifyPermission([PERMISSIONS.adminSecurityAccessmanagement]);

	const handleReload = () => {
		refetchBadges();
	};

	const handleBadgeRevoke = async (badgeId: string) => {
		await request
			.del(`/api/badges/${badgeId}`, {})
			.then(() =>
				EventEmitter.emit('toast', {
					type: 'success',
					message: 'Badge removed',
				}),
			)
			.catch(() =>
				EventEmitter.emit('toast', {
					type: 'error',
					message: 'Error deleting badge',
				}),
			);

		refetchBadges();
	};

	return (
		<BadgesTab
			userId={userId}
			badges={badges}
			tenantSecret={tenantSecret}
			readOnly={!hasFullAccess}
			onUpdate={handleReload}
			onBadgeRevoke={handleBadgeRevoke}
		/>
	);
}
